<template>
  <!-- Language Button -->
  <language-button @change-language="getBasePage"></language-button>
  <!-- Navigation Button -->
  <navi-button mode="middle"></navi-button>
  <!-- Social Media -->
  <base-social
    mode="social-finder"
    :share-url="shareUrl"
    :icon-class="iconClass"
    :button-class="buttonClass"
  >
  </base-social>
  <!-- Finder -->
  <div class="finder">
    <div class="header">
      <div class="header__logo">
        <base-logo mode="minerals__logo"></base-logo>
      </div>
      <h1 class="pageName heading-1">{{ getPageTitle }}</h1>
      <!-- Finder - Header -->

      <base-finderHeader
        @inputQuery="listenToQuery"
        :allSuggestions="getSuggestions"
        @submitQuery="listenToSpecificQuery"
      >
      </base-finderHeader>
    </div>
    <!-- Finder - Select -->
    <div class="select" id="select">
      <base-select
        v-for="option in getSelectOptions"
        :key="option[1][0]"
        :select-option="option"
        @selected-option="listenToSelect"
      >
      </base-select>
    </div>
    <!-- Finder Results -->
    <!-- Finder Results - Teleport -->
    <div class="results">
      <router-view></router-view>

      <div class="loadingStatus" :class="active"></div>

      <action-button @click="listenToRefresh" mode="refresh">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path
            d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm5 20l-1.359-2.038c-1.061.653-2.305 1.038-3.641 1.038-3.859 0-7-3.14-7-7h2c0 2.757 2.243 5 5 5 .927 0 1.786-.264 2.527-.708l-1.527-2.292h5.719l-1.719 6zm0-8c0-2.757-2.243-5-5-5-.927 0-1.786.264-2.527.708l1.527 2.292h-5.719l1.719-6 1.359 2.038c1.061-.653 2.305-1.038 3.641-1.038 3.859 0 7 3.14 7 7h-2z"
          />
        </svg>
      </action-button>

      <action-button @click="goToFilter" mode="filter">
        <!-- <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M1 0h22l-9 15.094v8.906l-4-3v-5.906z" />
        </svg> -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          class="size-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
          />
        </svg>
      </action-button>

      <action-button @click="goUp" mode="goUp">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="icon-upArrow"
          viewBox="0 0 24 24"
        >
          <path
            d="M24 12c0-6.627-5.373-12-12-12s-12 5.373-12 12 5.373 12 12 12 12-5.373 12-12zm-17 1h4v-8h2v8h4l-5 6-5-6z"
          />
        </svg>
      </action-button>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, ref, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useHead, useSeoMeta } from "@unhead/vue";
import { useRoute, useRouter } from "vue-router";

const store = useStore();
const route = useRoute();
const router = useRouter();

let startinglimit = ref(15); // infinite scrolling
let limit = ref(); //infinite scrolling
let active = ref(true); // infinite scrolling

/** social network buttons */
const shareUrl = ref({
  title: "lupanalua",
  text: "Minerals database searchable by type, composition and other characteristics",
  url: "https://www.lupanalua.com/minerals",
});

const buttonClass = {
  basic: "social--action-finder",
};
const iconClass = {
  basic: "icon--finder-basic",
};

/** SEO */
useSeoMeta({
  title:
    "Search for minerals by type, composition and other characteristics | lupanalua",
  description:
    "Minerals database searchable by type (ore or rock forming mineral), composition, crystal system, cleavage, fracture, streak color, lustre, density, or hardness. Lupanalua is a website for learners and people who are curious about the earth.",
  author: "lupanalua by Lu Shibata",
  ogTitle: "Search minerals | lupanalua website",
  ogType: "website",
  ogUrl: "https://www.lupanalua.com/minerals",
  ogSiteName: "lupanalua",
  ogImage: "https://www.lupanalua.com/public/img/og/minerals_1200x630.png",
});

useHead({
  link: [{ rel: "canonical", href: "https://lupanalua.com/minerals" }],
});

onBeforeMount(() => {
  getBasePage();
});

/**
 * calls function for triggering infinite scrolling
 */
onMounted(() => {
  observing();
});

/**
 * function that observes where the last element is positioned for lazy loading and infinite scrolling
 */
function observing() {
  const loading = document.querySelector(".loadingStatus");

  if (
    limit.value > getAllMinerals.value.length ||
    limit.value == getAllMinerals.value.length
  ) {
    active.value = false;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        limit.value += 10;
      }
      if (
        limit.value > getAllMinerals.value.length ||
        limit.value == getAllMinerals.value.length
      ) {
        active.value = false;
      }
    });
    // console.log("limit:" + limit.value);
    // console.log("total:" + getAllMinerals.value.length);
  });
  observer.observe(loading);
}

/** Computed **/

/**
 * @returns the whole page in the user language
 */
async function getBasePage() {
  store.dispatch("minerals/getPageTitle");
  store.dispatch("minerals/getSelectOptions");
  await store.dispatch("minerals/getMineralNames");
  if (route.path === "/minerals") {
    store.dispatch("minerals/getAllMinerals");
  } else {
    const one = store.getters["minerals/getOneMineral"].idName;
    await store.dispatch("minerals/getMineralFromParams", one);
  }
  if (route.path === "/minerals/filter") {
    store.dispatch("minerals/getFilterOptions");
  }
}

/**
 * @returns {String} of the user language
 */
//const getCurrentLanguage = computed(() => {
//  return store.getters.getCurrentLanguage;
//});

/**
 * @returns {String} of the page name in the user language
 */
const getPageTitle = computed(() => {
  return store.getters["minerals/getPageTitle"];
});

/**
 * @returns {Array} of mineral names in the user language, according to query
 */
const getSuggestions = computed(() => {
  return store.getters["minerals/getQuerySuggestions"];
});

/**
 * @returns {Array} of Select section in user language and english
 */
const getSelectOptions = computed(() => {
  return store.getters["minerals/loadSelectOptions"];
});
/**
 * @returns {Array} of mineral Objects in user language
 */
const getAllMinerals = computed(() => {
  return store.getters["minerals/getResults"];
});
/**
 * toggle visibility of the infinite scrolling threshold and the starting limit value
 */
watch(getAllMinerals, () => {
  console.log("observing getAll");
  active.value = true;
  limit.value = startinglimit.value;
});

/** Methods **/
/**
 * action button to go up
 */
function goUp() {
  window.scrollTo(0, 0);
}

/**
 * @param {String} q is the letter typed by user
 */
function listenToQuery(q) {
  router.push("/minerals");
  store.dispatch("minerals/getQuerySuggestions", q);
}
/**
 * action button to reset rendering all minerals
 */
function listenToRefresh() {
  store.dispatch("minerals/getAllMinerals");
  router.push("/minerals");
}
/**
 * @param {String} sq is the suggested mineral name that the user clicked
 */
function listenToSpecificQuery(sq) {
  document.querySelector("#app").style.scale = 1.0;
  store.dispatch("minerals/getOneMineral", sq);
}
/**
 * @param {Array} qa of select and clicked option in english, e.g.: ['cleavage', 'perfect'], ['density', 9]
 */
function listenToSelect(qa) {
  store.dispatch("minerals/getSelect", [qa]);
}

/**
 * show filter
 */
function goToFilter() {
  if (/^\/minerals\/[^/]+$/.test(route.path)) {
    return window.open("/minerals/filter");
  }
  router.push("minerals/filter");
}
</script>

<style scoped>
.select {
  display: grid;
  grid-template-columns: repeat(9, minmax(max-content, 1fr));
  column-gap: 2px;
}
@media only screen and (max-width: 56.25em) {
  .select {
    display: none;
  }
}

.icon-upArrow {
  transform: rotate(180deg);
}

/* animation for filter*/

.openFilter-enter-from {
  transform: translateX(10rem);
}
.openFilter-enter-active {
  transition: all 0.5s ease-out;
}
.openFilter-enter-to {
  transform: translateX(0rem);
}

/* animation for error */
.sendError-enter-from {
  transform: translateY(10rem);
}
.sendError-enter-active {
  transition: all 0.5s ease-out;
}

.sendError-enter-to {
  transform: translateY(0rem);
}

.loadingStatus {
  visibility: hidden;
  text-align: center;
  font-weight: 400;
  margin: 0.5rem;
  padding: 1rem;
  width: 0.5rem;
  height: 0.5rem;
  background-color: white;
  font-size: 2rem;
}

.active {
  visibility: visible;
}
.heading-1 {
  color: var(--color-grey-4);
  font-family: var(--font-display);
  font-weight: 400;
}

.heading-3 {
  color: var(--color-grey-4);
  font-family: var(--font-display);
  font-weight: 400;
}

.heading-4 {
  color: var(--color-grey-4);
  font-family: var(--font-display);
  font-weight: 400;
}

.finder {
  display: grid;
  grid-template-rows:
    [header-start] 15vh [header-end select-start] 5vh [select-end results-start] minmax(
      80vh,
      auto
    )
    [results-end];
  position: relative;
  width: 100vw;
}
@media only screen and (max-width: 56.25em) {
  .finder {
    grid-template-rows:
      [header-start] 15vh [header-end results-start] minmax(85vh, auto)
      [results-end];
  }
}

.header {
  grid-row: header-start / header-end;
  position: fixed;
  height: 15vh;
  z-index: 1000;
  display: grid;
  grid-template-columns: [logo-start]8vw[logo-end heading-start] 37vw [heading-end search-start] 55vw [search-end];
  grid-template-rows: 70% 30%;
}
@media only screen and (max-width: 56.25em) {
  .header {
    grid-template-columns: [logo-start] 12vw [logo-end heading-start] 88vw [heading-end];
    border-bottom: 1px solid var(--color-white);
  }
}

.header__logo {
  animation: leftToRight 2s linear;
  grid-column: logo-start / logo-end;
  grid-row: 1 / -1;
  align-self: center;
  justify-self: center;
}
@media only screen and (max-width: 26.5em) {
  .header__logo {
    grid-row: 1/2;
  }
}

.pageName {
  text-transform: capitalize;
  grid-column: heading-start / heading-end;
  grid-row: 1/-1;
  align-self: end;
  justify-self: end;
  animation: headerDown 2s ease-in;
}
@media only screen and (max-width: 56.25em) {
  .pageName {
    grid-column: logo-start/search-end;
    grid-row: 1 / 2;
    align-self: flex-end;
    justify-self: center;
  }
}
@media only screen and (max-width: 26.5em) {
  .pageName {
    align-self: flex-end;
  }
}

.select {
  grid-row: select-start / select-end;
}

@media only screen and (max-width: 56.25em) {
  #select {
    display: none;
  }
}

.results {
  grid-row: results-start/results-end;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-right: 4rem;
}

/* 900px*/
@media only screen and (max-width: 56.25em) {
  .results {
    align-items: flex-start;
  }
}
@media only screen and (max-width: 23.75em) {
  .results {
    padding-right: 3rem;
  }
}

/** MINERALS header */
.header {
  background-color: var(--color-primary-2);
}
/** MINERALS select */
.select {
  display: grid;
  grid-template-columns: repeat(9, minmax(max-content, 1fr));
  column-gap: 2px;
  background-color: var(--color-white);
  border-bottom: 1px solid var(--color-grey-3);
  margin-right: 1rem;
}

@media only screen and (max-width: 56.25em) {
  .select {
    display: none;
  }
}
/** MINERALS results */
.results {
  background-image: linear-gradient(
    to bottom,
    var(--color-primary-2),
    var(--color-primary-3)
  );
}
@media only screen and (max-width: 48em) {
  .minerals > div:nth-child(3) {
    align-items: flex-start;
  }
}
/* Animations */
@keyframes headerDown {
  0% {
    transform: translateY(-5rem);
  }
  100% {
    transform: translateY(0rem);
  }
}
</style>
